/**
 * Breaking news on held and BUY names, with the move since each story landed.
 *
 * 🔴 **Why this screen exists, and it was measured.** On 2026-08-19 Merck and Moderna published
 * Phase 3 topline results at 10:47 UTC — two hours 43 minutes before the US open — and Moderna went
 * 62.93 to 189.21. The screener had run at 06:15 off the previous session's close and priced it at
 * 62.93; there was no bar for that day at all. A job that publishes before the open cannot report
 * the session it precedes, so nothing on the Screener could ever have shown this.
 *
 * ⛔ **Nothing here is a signal or a recommendation.** No rule reads these rows and no verdict
 * moves. A headline is not a direction — the same feed carries a trial success and an FDA rejection
 * in the same shape. The screen says *go and look*.
 *
 * ⚠ **"Live" means the poller's cadence, which is two minutes**, and the screen says so rather than
 * implying a tick-by-tick feed. Every row carries the time its price was read, because a reader who
 * cannot see that cannot tell a moving number from a stalled one.
 */
import type { JSX } from 'react';

import { EmptyState } from '../../design/components/messages/EmptyState.jsx';
import { NewsDesk } from './NewsDesk.jsx';
import type { NewsDeskCounts } from './NewsDesk.jsx';
import { Num } from '../../design/components/data/Num.jsx';

/**
 * Whether a price reading is old enough that the percentage beside it should be read with care.
 *
 * ⚠ Five minutes, not one: the poller runs every minute, so two or three missed ticks is a blip
 * while half an hour means the market is shut or the poller is down — and those look identical on
 * a row without this.
 */
function stale(iso: string | null | undefined): boolean {
  if (!iso) return true;
  return Date.now() - new Date(iso).getTime() > 5 * 60_000;
}

function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 90) return `${Math.round(secs)}s ago`;
  if (secs < 5400) return `${Math.round(secs / 60)}m ago`;
  // ⚠ Days and years exist because they had to. Without them SBIG's last trade — measured at
  // 1,083 days old — rendered as "26000h ago", which reads like a glitch rather than a fact.
  if (secs < 172_800) return `${Math.round(secs / 3600)}h ago`;
  if (secs < 63_072_000) return `${Math.round(secs / 86_400)}d ago`;
  return `${(secs / 31_536_000).toFixed(1)}y ago`;
}

export type NewsSort = 'recent' | 'impact' | 'expected';

export function NewsScreen({
  items,
  sort = 'recent',
  onSort,
  desk = null,
}: {
  items: NewsItem[];
  sort?: NewsSort;
  onSort?: (next: NewsSort) => void;
  /** D82: the freshness contract and the act-now split that now lead the page. */
  desk?: NewsDeskCounts | null;
}): JSX.Element {
  if (items.length === 0) {
    return (
      <div className="jmt-screen">
        <div className="jmt-note">
          <EmptyState>
            No news on your held or BUY names in the last 48 hours. The poller runs every two
            minutes on weekdays; nothing to show is different from nothing running — the Telegram
            channel gets the same stories, so silence here should match silence there.
          </EmptyState>
        </div>
      </div>
    );
  }
  return (
    <div className="jmt-screen">
      {/* 🔴 **The desk leads, and the sortable feed stays below it as the full record.** D82 split
          this page: the handful of stories a message was sent for are a different kind of thing
          from the 1,100 that arrived in the same window, and one list ordered by recency treats
          them identically. The feed is not removed, because *"show me everything, by impact"* is a
          real question and the desk deliberately answers a different one. */}
      <NewsDesk desk={desk} items={items} />

      <h2 className="jmt-stk-nd__feedhead">The full feed</h2>
      {onSort ? (
        <div className="jmt-stk-tabs" role="tablist" aria-label="How to order the feed">
          {(
            [
              ['recent', 'Latest', 'newest first — what just happened'],
              ['impact', 'Impact', 'largest measured move, in this company&rsquo;s own sigmas'],
              ['expected', 'Expected', 'most material kind of event, before any price moved'],
            ] as const
          ).map(([key, label, hint]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={sort === key}
              title={hint}
              className={`jmt-stk-tab${sort === key ? ' jmt-stk-tab--on' : ''}`}
              onClick={() => onSort(key)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      <div className="jmt-stk-news">
        {items.map((item) => (
          <article className="jmt-stk-news__row" key={item.articleId}>
            <div className="jmt-stk-news__head">
              <div className="jmt-stk-news__who">
                <span className="jmt-stk-news__name">{item.name ?? item.symbol ?? '—'}</span>
                {item.symbol ? <code className="jmt-stk-news__sym">{item.symbol}</code> : null}
                {item.eventClass && item.eventClass !== 'other' ? (
                  <span className="jmt-stk-news__class">{item.eventClass.replace(/_/g, ' ')}</span>
                ) : null}
              </div>

              {/* 🔴 The move since the story landed — the number this screen exists for, so it is
                  the largest thing on the row and the only one that carries direction colour.
                  `Num` owns the sign, the arrow and the ramp. */}
              <div className="jmt-stk-news__figure">
                {item.pctSinceNews == null ? (
                  <span className="jmt-stk-news__unknown" title="No baseline price for this story">
                    —
                  </span>
                ) : item.priceStale ? (
                  /* 🔴 **The price is older than the last completed session, so this is not a
                      move.** Alpaca's IEX feed returns a symbol's last trade whenever it happened;
                      SBIG's was 1,083 days old and PRPH's 229. Because the baseline was captured
                      from that same ancient print, the arithmetic produces a confident 0.0% for a
                      stock that has not traded in years.

                      ⛔ So the number is withheld rather than styled. A greyed-out "0.0%" is still
                      a figure a reader can act on, and this one means nothing at all.

                      ⚠ This is NOT the overnight case. Every price is hours old before the open —
                      the free feed has no extended-hours prints — and those rows keep their big
                      number, because the operator asked for exactly that and was right to. */
                  <span
                    className="jmt-stk-news__nostale"
                    title={`No recent price: the last trade was ${ago(item.lastPriceAt)}, which is before the last completed session. Any percentage would be arithmetic on a dead quote.`}
                  >
                    no recent price
                  </span>
                ) : (
                  <Num value={Number(item.pctSinceNews)} kind="percent" dp={1} arrow step="display" />
                )}
                {/* ⚠ The session move, shown only when the news-relative number has nothing to
                    say yet. Labelled "session" because it is the day, not this story. */}
                {!item.priceStale &&
                item.pctSinceNews != null &&
                Number(item.pctSinceNews) === 0 &&
                item.sessionPct != null ? (
                  <div className="jmt-stk-news__session">
                    session <Num value={Number(item.sessionPct)} kind="percent" dp={1} arrow step="sm" />
                  </div>
                ) : null}
                <div className="jmt-stk-news__stamps">
                  {/* ⚠ Two ages, and they answer different questions. "12m ago" is how old the
                      NEWS is; "price 8h ago" is how old the PRICE behind that percentage is. When
                      the second is much older than the first the market has not traded since the
                      story, which is why the number can honestly be 0.0%. */}
                  <span title="How long ago this story was published">
                    {ago(item.publishedAt)}
                  </span>
                  {/* ⚠ Two different kinds of old, and they must not look the same. `priceStale`
                      means the price predates the last SESSION and is not usable at all;
                      `stale()` means merely more than five minutes, which overnight is every row
                      and is perfectly normal. */}
                  <span
                    className={
                      item.priceStale
                        ? 'jmt-stk-news__nostale'
                        : stale(item.lastPriceAt)
                          ? 'jmt-stk-news__stale'
                          : undefined
                    }
                    title={
                      item.priceStale
                        ? 'This price predates the last completed session — no weekend or holiday explains it, so the instrument is not trading.'
                        : 'When the price behind this percentage was last read'
                    }
                  >
                    {item.priceStale ? 'last traded ' : 'price '}
                    {ago(item.lastPriceAt)}
                  </span>
                </div>
              </div>

              {/* The impact score: how unusual that move is for THIS company, 0-100. Deliberately
                  not colour-coded — it is an absolute magnitude, and green/red would imply it says
                  whether the news was good. */}
              {/* Two scores, and they answer different questions.

                  EXPECTED is how material this kind of news usually is for a company this size —
                  no price needed, so it is there immediately. ⛔ Not a direction forecast: an
                  approval and a rejection score identically.

                  REALISED is what the price actually did, in this company's own daily sigmas. It
                  is absent until something trades, which is most of the night. */}
              <div className="jmt-stk-news__scores">
                <div
                  className="jmt-stk-news__impact"
                  title={
                    item.expectedImpact == null
                      ? 'No expected score: the story is not classified yet'
                      : 'How material this KIND of news usually is for a company this size. Not a direction forecast.'
                  }
                >
                  {item.expectedImpact == null ? (
                    <span className="jmt-stk-news__unknown">—</span>
                  ) : (
                    <b>{item.expectedImpact}</b>
                  )}
                  <span className="jmt-stk-news__impact-of">expected</span>
                </div>
                <div
                  className="jmt-stk-news__impact"
                  title={
                    item.impactScore == null
                      ? 'Not measurable yet: nothing has traded since this story, or no volatility history'
                      : `${item.moveSigma ?? '?'} daily sigmas for this company`
                  }
                >
                  {item.impactScore == null ? (
                    <span className="jmt-stk-news__unknown">—</span>
                  ) : (
                    <b>{item.impactScore}</b>
                  )}
                  <span className="jmt-stk-news__impact-of">realised</span>
                </div>
              </div>
            </div>
            <p className="jmt-stk-news__headline">
              {/* ⚠ A feed is a reading surface; a headline you cannot open is one you have to go
                  and search for. `rel` is set because these are third-party links. */}
              {item.url ? (
                <a href={item.url} target="_blank" rel="noreferrer noopener">
                  {item.headline}
                </a>
              ) : (
                item.headline
              )}
            </p>
            <div className="jmt-stk-news__meta">
              <span>
                {item.baselinePrice ?? '—'} → {item.lastPrice ?? '—'}
              </span>
              {item.source ? (
                <>
                  <span>·</span>
                  <span>{item.source}</span>
                </>
              ) : null}
              {item.lagSeconds != null ? (
                <>
                  <span>·</span>
                  <span title="Seconds from publication to this system receiving it">
                    ingested in {item.lagSeconds}s
                  </span>
                </>
              ) : null}
              {/* ⚠ Only shown when the FALLBACK priced it. `alpaca` is the normal case and
                  saying so on every row would be noise; `yahoo` means the primary had nothing
                  fresh, which is worth knowing. */}
              {item.priceSource === 'yahoo' ? (
                <>
                  <span>·</span>
                  <span title="Priced from the secondary feed — the primary had no fresh print">
                    via yahoo
                  </span>
                </>
              ) : null}
              {item.alerted ? (
                <>
                  <span>·</span>
                  <span className="jmt-stk-news__alerted">alerted</span>
                </>
              ) : null}
            </div>
          </article>
        ))}
      </div>
      <p className="jmt-stk-news__foot">
        Streamed continuously, with a one-minute reconciliation poll that also refreshes every
        price. <b>Impact is measured, not predicted</b> — the move since the story landed, in this
        company&rsquo;s own daily standard deviations. A story that moved nothing scores low because
        the market disagreed it mattered.{' '}
        {/* 🔴 **Read from the desk, never written here.** This sentence said "Telegram fires only on
            a 3% move" until D82 — the floor was raised to 8% and a 4.0σ condition added on
            2026-08-20, so the page had been telling the operator a gate nobody was filtering on.
            Hardcoding a threshold beside a tunable one is how that happens; taking it from the same
            query the strip uses is how it stops. */}
        {desk
          ? `Telegram fires only on a move of at least ${desk.gatePct.toFixed(1)}% that is also
             at least ${desk.gateSigma.toFixed(1)} of this company's own daily sigmas.`
          : 'Telegram fires on both a size and a significance condition; see the strip above.'}{' '}
        Not a signal: no rule reads these and no verdict changes.
      </p>
    </div>
  );
}

export interface NewsItem {
  /**
   * How many OTHER stories describe the same company-day move. D82.
   *
   * ⚠ One company's move is ONE message: the poller collapses a company-day with `distinct on`
   * because the vendor files many stories per move — Advance Auto Parts fell 15.6% and four
   * separate headlines were filed. Without this count the surviving alert reads as the only story
   * about the event, which is how a reader concludes the vendor is quieter than it is.
   */
  siblingHeadlines?: number;
  articleId: string;
  headline: string;
  symbols: string[];
  symbol?: string | null;
  name?: string | null;
  isin?: string | null;
  publishedAt: string;
  baselinePrice?: string | null;
  lastPrice?: string | null;
  lastPriceAt?: string | null;
  pctSinceNews?: string | null;
  url?: string | null;
  source?: string | null;
  /**
   * Whether this story escalated to Telegram.
   *
   * ⚠ **A record, not a rule.** It says a message went out under whichever gate was live at that
   * moment — which is not the same as "meets the gate now": both thresholds were retuned on
   * 2026-08-20, and of 77 alerted rows in the following 48 hours only 26 meet the current 4.0σ.
   * This comment said "only a >=3% move does", which stopped being true that day.
   */
  alerted?: boolean;
  /** Publication to receipt, in seconds — the stream's latency. */
  lagSeconds?: number | null;
  /**
   * 🔴 0-100 from what the price DID since the story landed, normalised by this company's own
   * daily volatility. ⛔ Not a forecast — an FDA approval that moved nothing scores low, and
   * that is the market disagreeing about significance rather than a bug.
   * ⚠ `null` means not measurable, never 0.
   */
  impactScore?: number | null;
  eventClass?: string | null;
  /** The move in this company's own daily sigmas, unsaturated. */
  moveSigma?: string | null;
  /**
   * The last completed session's move.
   *
   * ⚠ A DIFFERENT measurement from pctSinceNews and never a substitute: that one attributes a move
   * to a story, this is just the day. It exists because the free IEX feed carries no pre-market
   * prints, so an overnight story reads 0.0% until the open.
   */
  sessionPct?: string | null;
  sessionDay?: string | null;
  /**
   * ⛔ How material this KIND of news usually is for a company this size, 0-100. Needs no
   * price, so it is there the instant a headline lands — including pre-market, where
   * impactScore cannot exist. NOT a direction forecast: an approval and a rejection score
   * the same. Judgement, not measurement.
   */
  expectedImpact?: number | null;
  marketCap?: number | null;
  /** `alpaca` (primary) or `yahoo` (secondary, when the primary had nothing fresh). */
  priceSource?: string | null;
  /**
   * 🔴 `true` when `lastPrice` predates the last completed session, so no percentage computed from
   * it means anything. ⚠ Not the overnight case — hours-old prices before the open are normal and
   * are not flagged.
   */
  priceStale?: boolean | null;
}
